import React, { useEffect, useState, useRef } from 'react';
import { logger } from '@ui/utils/logger';
import mammoth from 'mammoth';
import ViewerHeader from './ViewerHeader';
import Styles from './Styles.module.scss';

interface DocxViewerProps {
    fileUrl: string;
    highlightKeywords?: string[];
}

const DocxViewer: React.FC<DocxViewerProps> = ({ fileUrl, highlightKeywords = [] }) => {
    const [html, setHtml] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const loadDocx = async () => {
            if (!fileUrl) return;
            try {
                const response = await fetch(fileUrl);
                const arrayBuffer = await response.arrayBuffer();
                const { value } = await mammoth.convertToHtml({ arrayBuffer });
                setHtml(value);
            } catch (error) {
                logger.error("Docx couldn't load", error);
            }
        };
        loadDocx();
    }, [fileUrl]);

    const highlightHtml = (html: string, keywords: string[]) => {
        if (!keywords.length) return html;

        const escapedKeywords = keywords.filter(Boolean).map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));

        if (!escapedKeywords.length) return html;

        const regex = new RegExp(`(${escapedKeywords.join('|')})(?![^<]*>)`, 'gi');
        return html.replace(regex, `<mark class="highlight">$1</mark>`);
    };

    const finalHtml = highlightHtml(html, highlightKeywords);

    useEffect(() => {
        if (!highlightKeywords.length || !containerRef.current) return;

        const firstKeyword = highlightKeywords[0].toLowerCase();

        const marks = Array.from(containerRef.current.querySelectorAll<HTMLElement>('mark.highlight'));

        const target = marks.find(mark => mark.textContent?.toLowerCase().includes(firstKeyword));

        if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [highlightKeywords, finalHtml]);

    return (
        <div style={{ height: 'calc(100% - 60px)' }}>
            <ViewerHeader />
            <div
                ref={containerRef}
                className={Styles.docxHighligtedText}
                dangerouslySetInnerHTML={{ __html: finalHtml }}
            />
            <style>{`
        .highlight {
          background-color: yellow;
          color: black;
        }
      `}</style>
        </div>
    );
};

export default DocxViewer;
